import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { Pad } from '@brighterly/lib-core-types/plans/interfaces'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import type { AxiosError } from 'axios'

import { useCustomerStore } from '@stores/me/customer'

import { useCsRoutes } from '@composables/routes/cs-route.hook'
import { useDiscipline } from '@hooks/discipline/index.hook'

interface PadApiPayload {
  discipline: Discipline
  to: RouteLocationNormalizedGeneric
}

interface PadApiOptions {
  onStart?: () => void
  onSuccess?: (pad: Pad) => void
  onError?: (err: AxiosError) => void
  onFinish?: () => void
}

export const usePlansApi = async (payload: PadApiPayload, options?: PadApiOptions): Promise<Pad | null> => {
  const { $axios } = useNuxtApp()

  const customerStore = useCustomerStore()

  const {
    isDisciplineEla,
  } = useDiscipline({
    discipline: payload.discipline,
  })

  const padRoute = isDisciplineEla.value ? 'pad.ela' : 'pad.math'
  const pricingRoute = isDisciplineEla.value ? 'pricing.ela' : 'pricing.math'

  const offerHash = (payload.to.query?.offer_hash || null) as string | null

  const query: Record<string, string> = offerHash ? { offer_hash: offerHash } : {}

  const url = useCsRoutes(customerStore.isTypePaid ? pricingRoute : padRoute, query)

  options?.onStart?.()

  try {
    const { data } = await $axios.get(url!)
    const pad = data.data as Pad

    options?.onSuccess?.(pad)
    return pad

  } catch (err) {
    console.error(err)
    options?.onError?.(err as AxiosError)
    return null
  } finally {
    options?.onFinish?.()
  }
}
